import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { AppLayout } from "../components/layout/AppLayout";
import { FilterSheet } from "../components/layout/FilterSheet";
import { FilterSidebar } from "../components/filters";
import { EventList, CalendarView } from "../components/calendar";
import { MapView } from "../components/map";
import { NearbyView } from "../components/nearby";
import { UnplaceableNote } from "../components/nearby/UnplaceableNote";
import { useFavorites } from "../hooks/useFavorites";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { groupIntoSeries, summarize, countThrough, isoMonthsFromNow, seriesLabel } from "../lib/series";
import { useDebounce } from "../hooks/useDebounce";
import { isValidDistance } from "../lib/distance";
import { withDistance, withinRadius } from "../lib/nearby";
import { useViewMode } from "../contexts/ViewModeContext";
import type { GeocodingResult } from "../lib/geocoding";
import { EventsJsonLd } from "../components/seo/EventsJsonLd";
import { cn } from "../lib/utils";

export function HomePage() {
  /**
   * Zip, radius and styles start from the URL, so a search survives a reload
   * and can be handed to someone else. All are validated rather than trusted
   * — ?within=9999 is not one of the radii the picker offers.
   */
  const initialParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  // Filter state
  const [selectedStyles, setSelectedStyles] = useState<string[]>(() =>
    (initialParams.get("style") ?? "")
      .split(",")
      .filter((slug) => /^[a-z0-9-]+$/.test(slug))
  );
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [zipCode, setZipCode] = useState(() => {
    const zip = initialParams.get("zip") ?? "";
    return /^\d{5}$/.test(zip) ? zip : "";
  });
  const [distance, setDistance] = useState(() => {
    const raw = initialParams.get("within");
    const parsed = raw === null ? NaN : Number(raw);
    return Number.isFinite(parsed) && isValidDistance(parsed) ? parsed : 25;
  });
  const [userLocation, setUserLocation] = useState<GeocodingResult | null>(null);

  /**
   * A signed-in visitor picks up where they left off.
   *
   * Their last zip is on their account, so it follows them from laptop to
   * phone. An explicit ?zip= in the link always wins — someone who followed a
   * link to dances near Providence means Providence, not wherever they
   * personally last searched. Restored once, so it never fights an edit.
   */
  const { user } = useCurrentUser();
  const restoredFromAccount = useRef(false);
  const urlHadZip = useMemo(() => !!initialParams.get("zip"), [initialParams]);

  useEffect(() => {
    if (restoredFromAccount.current || urlHadZip) return;
    if (!user?.lastZipCode) return;
    restoredFromAccount.current = true;
    setZipCode(user.lastZipCode);
    if (user.lastDistanceMiles !== undefined && isValidDistance(user.lastDistanceMiles)) {
      setDistance(user.lastDistanceMiles);
    }
  }, [user, urlHadZip]);

  // And remember it for next time, once it has actually resolved to somewhere.
  // The mutation ignores signed-out visitors and unchanged values, so this can
  // fire freely without writing on every render.
  const setLastZipSearch = useMutation(api.users.setLastZipSearch);
  useEffect(() => {
    if (!userLocation || zipCode.length !== 5) return;
    void setLastZipSearch({ zipCode, distanceMiles: distance }).catch(() => {
      // Remembering is a convenience; failing to is not worth surfacing.
    });
  }, [userLocation, zipCode, distance, setLastZipSearch]);

  // Keep the URL in step with the location and style filters. replaceState
  // rather than pushState: retyping a zip should not queue up a dozen
  // back-button steps.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (zipCode.length === 5) {
      params.set("zip", zipCode);
      params.set("within", String(distance));
    } else {
      params.delete("zip");
      params.delete("within");
    }
    if (selectedStyles.length > 0) params.set("style", selectedStyles.join(","));
    else params.delete("style");
    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      query ? `${window.location.pathname}?${query}` : window.location.pathname
    );
  }, [zipCode, distance, selectedStyles]);

  // Shared event link: read ?event=<id> from URL
  const sharedEventId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("event") as Id<"events"> | null;
  }, []);
  const sharedEvent = useQuery(
    api.events.getEvent,
    sharedEventId ? { eventId: sharedEventId } : "skip"
  );

  // View state from shared context
  const { viewMode } = useViewMode();
  const [calendarAnchor, setCalendarAnchor] = useState(() => new Date());

  // Mobile filter sheet state
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  /**
   * Collapsed to one row per series, or every individual date.
   *
   * Collapsing is right for the common question — "what is on this week" —
   * but it shows only each series' next night, so a date that was moved, or a
   * note attached to one occurrence, is invisible behind the row standing in
   * for it. All-dates is how you get at those.
   */
  const [showAllDates, setShowAllDates] = useState(false);

  const { favorites } = useFavorites();

  // Debounced search query for filtering
  const debouncedSearch = useDebounce(searchQuery, 300);

  const events = useQuery(api.events.getUpcomingEvents, {
    danceStyles: selectedStyles.length > 0 ? selectedStyles : undefined,
  });

  // Handle location change from ZipCodeInput
  const handleLocationChange = useCallback((location: GeocodingResult | null) => {
    setUserLocation(location);
  }, []);

  // Client-side filtering: search, favorites, distance, date range
  const { filteredEvents, unplaceableCount } = useMemo(() => {
    if (!events) return { filteredEvents: [], unplaceableCount: 0 };

    let result = [...events];

    // Filter by date range
    if (startDate) {
      result = result.filter((event) => event.date >= startDate);
    }
    if (endDate) {
      result = result.filter((event) => event.date <= endDate);
    }

    // Filter by favorites. An occurrence of a followed series counts, and so
    // does an event at a followed venue — following a venue is a statement
    // about wanting to see what happens there.
    if (showFavoritesOnly) {
      result = result.filter(
        (event) =>
          favorites.eventIds.includes(event._id) ||
          (event.patternId && favorites.seriesIds.includes(event.patternId)) ||
          (event.venueId && favorites.venueIds.includes(event.venueId))
      );
    }

    // Filter by search query (title, venue, address)
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      result = result.filter(
        (event) =>
          event.title.toLowerCase().includes(query) ||
          event.venueName.toLowerCase().includes(query) ||
          (event.venueAddress?.toLowerCase().includes(query) ?? false)
      );
    }

    // Measure from the searcher's zip, and drop what falls outside the radius.
    //
    // Events whose venue has no coordinates cannot be measured at all. They are
    // counted rather than quietly discarded: "no location on file" and "too far
    // away" look identical in a filtered list, and only one of them is the
    // reader's own doing.
    if (!userLocation) {
      return { filteredEvents: result, unplaceableCount: 0 };
    }

    const { located, missingCoords } = withDistance(result, userLocation);
    return {
      filteredEvents: withinRadius(located, distance),
      unplaceableCount: missingCoords.length,
    };
  }, [events, showFavoritesOnly, favorites, debouncedSearch, userLocation, distance, startDate, endDate]);

  /**
   * The list and its count work in series, not occurrences.
   *
   * `extendRecurringSeries` keeps 20 upcoming dates per series, so eleven real
   * events were being reported as ~200 and a monthly dance filled the list with
   * the same night twenty times. Each series collapses to its next occurrence,
   * which also carries that date's own details — this month's band, a parking
   * note — so per-date variation still shows without splitting the series up.
   *
   * Collapsing happens after filtering, so a date or distance filter still
   * decides which occurrences are eligible in the first place.
   */
  const seriesList = useMemo(
    () => groupIntoSeries(filteredEvents),
    [filteredEvents]
  );

  const collapsedEvents = useMemo(
    () => seriesList.map((s) => s.next),
    [seriesList]
  );

  /** Keyed by the entry standing in for each series — its next occurrence. */
  const collapsedSeriesInfo = useMemo(() => {
    const through = isoMonthsFromNow(3);
    return new Map(
      seriesList.map((s) => [
        s.next._id as string,
        {
          cadenceLabel: seriesLabel(s),
          countNext3Months: countThrough(s, through),
          isRecurring: s.isRecurring,
        },
      ])
    );
  }, [seriesList]);

  /**
   * In all-dates mode every occurrence gets the cadence of the series it
   * belongs to, so a card still says what it is part of — but not a count of
   * dates the reader can already see.
   */
  const allDatesSeriesInfo = useMemo(() => {
    const entries: [string, { cadenceLabel: string; isRecurring: boolean }][] = [];
    for (const s of seriesList) {
      const label = seriesLabel(s);
      for (const occurrence of s.occurrences) {
        entries.push([
          occurrence._id as string,
          { cadenceLabel: label, isRecurring: s.isRecurring },
        ]);
      }
    }
    return new Map(entries);
  }, [seriesList]);

  const seriesInfo = showAllDates ? allDatesSeriesInfo : collapsedSeriesInfo;

  const counts = useMemo(() => summarize(seriesList), [seriesList]);

  // Common filter props
  const filterProps = {
    searchQuery,
    onSearchChange: setSearchQuery,
    startDate,
    endDate,
    onStartDateChange: setStartDate,
    onEndDateChange: setEndDate,
    zipCode,
    onZipCodeChange: setZipCode,
    distance,
    onDistanceChange: setDistance,
    location: userLocation,
    onLocationChange: handleLocationChange,
    selectedStyles,
    onStyleChange: setSelectedStyles,
    showFavoritesOnly,
    onFavoritesChange: setShowFavoritesOnly,
  };

  return (
    <AppLayout
      sidebar={<FilterSidebar {...filterProps} />}
    >
      <EventsJsonLd events={filteredEvents} />
      <FilterSheet
        {...filterProps}
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
      />
      {/* Event count, and the collapse toggle beside it. The nearby tab states
          its own count, in terms of where it is measuring from. */}
      <div
        className={cn(
          "items-center justify-between gap-3 mb-6",
          viewMode === "nearby" ? "hidden" : "flex"
        )}
      >
        <div className="text-sm text-muted-foreground">
          {showAllDates ? (
            <>
              {filteredEvents.length} date{filteredEvents.length !== 1 ? "s" : ""}
              {counts.total > 0 && ` · ${counts.total} event${counts.total !== 1 ? "s" : ""}`}
            </>
          ) : (
            <>
              {counts.total} event{counts.total !== 1 ? "s" : ""}
              {counts.total > 0 && (
                <>
                  {" · "}
                  {counts.recurring > 0 && `${counts.recurring} recurring`}
                  {counts.recurring > 0 && counts.oneOff > 0 && ", "}
                  {counts.oneOff > 0 && `${counts.oneOff} one-off`}
                </>
              )}
            </>
          )}
        </div>

        {viewMode === "list" && counts.recurring > 0 && (
          <button
            type="button"
            onClick={() => setShowAllDates((v) => !v)}
            className="flex-shrink-0 text-sm font-medium text-primary hover:underline"
          >
            {showAllDates ? "Show series" : "Show all dates"}
          </button>
        )}
      </div>

      {/* The nearby tab prints this itself, in its own header block. */}
      {viewMode !== "nearby" && (
        <UnplaceableNote count={unplaceableCount} className="-mt-3 mb-5" />
      )}

      {/* Conditional view rendering */}
      {viewMode === "list" && (
        /* The list gets its own scroll region so a few hundred events don't turn
           the page into an endless scroll — scrolling outside it moves the page. */
        <div className="max-h-[calc(100dvh-12rem)] sm:max-h-[calc(100dvh-15rem)] overflow-y-auto rounded-xl border border-border/50 bg-secondary/20 px-3 py-4 sm:px-4">
          <EventList
            events={showAllDates ? filteredEvents : collapsedEvents}
            isLoading={events === undefined}
            selectedStyle={selectedStyles.length === 1 ? selectedStyles[0] : null}
            initialEvent={sharedEvent ?? undefined}
            seriesInfo={seriesInfo}
          />
        </div>
      )}
      {viewMode === "calendar" && (
        <CalendarView
          events={filteredEvents}
          anchorDate={calendarAnchor}
          onAnchorChange={setCalendarAnchor}
          initialEvent={sharedEvent ?? undefined}
        />
      )}
      {viewMode === "map" && (
        <MapView
          events={filteredEvents}
          isLoading={events === undefined}
          initialEvent={sharedEvent ?? undefined}
        />
      )}
      {viewMode === "nearby" && (
        <NearbyView
          events={collapsedEvents}
          isLoading={events === undefined}
          seriesInfo={collapsedSeriesInfo}
          initialEvent={sharedEvent ?? undefined}
          unplaceableCount={unplaceableCount}
          zipCode={zipCode}
          onZipCodeChange={setZipCode}
          distance={distance}
          onDistanceChange={setDistance}
          location={userLocation}
          onLocationChange={handleLocationChange}
        />
      )}
    </AppLayout>
  );
}
